import { calculatePixelsPerMm, detectScreenDPI } from "./coordinateTransform";

export const DEFAULT_SCREEN_DPI = 96;
export const MIN_VALID_DPI = 30;
export const MAX_VALID_DPI = 500;
export const DEFAULT_CALIBRATION_BAR_PX = 240;
export const SCREEN_CALIBRATION_SESSION_KEY = "eeg-screen-calibration-v1";
export const SCREEN_CALIBRATION_ENV_SESSION_KEY =
  "eeg-screen-calibration-env-v1";
export const SCREEN_CALIBRATION_LOCAL_KEY = "eeg-screen-calibration-v2";
export const SCREEN_CALIBRATION_ENV_LOCAL_KEY = "eeg-screen-calibration-env-v2";

export interface ScreenCalibrationSessionState {
  manualOverrideEnabled: boolean;
  horizontalMm: number | null;
  verticalMm: number | null;
}

export interface ScreenCalibrationEnvironmentSnapshot {
  devicePixelRatio: number;
  visualViewportScale: number;
  innerWidth: number;
  innerHeight: number;
  screenWidth: number;
  screenHeight: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));

export const normalizeMeasuredMm = (
  value: number | null | undefined,
): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value <= 0) return null;
  return clamp(value, 1, 1000);
};

export const normalizeDpi = (value: number | null | undefined): number => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_SCREEN_DPI;
  }
  return clamp(value, MIN_VALID_DPI, MAX_VALID_DPI);
};

export const safeDetectScreenDpi = (): number => {
  try {
    return normalizeDpi(detectScreenDPI());
  } catch {
    return DEFAULT_SCREEN_DPI;
  }
};

export const calculateDpiFromMm = (
  measuredMm: number | null,
  barLengthPx: number = DEFAULT_CALIBRATION_BAR_PX,
): number | null => {
  const safeMm = normalizeMeasuredMm(measuredMm);
  if (!safeMm) return null;
  if (!Number.isFinite(barLengthPx) || barLengthPx <= 0) return null;
  return normalizeDpi((barLengthPx * 25.4) / safeMm);
};

export const resolveEffectiveScreenDpi = (
  autoDetectedDpi: number,
  calibration: ScreenCalibrationSessionState,
  barLengthPx: number = DEFAULT_CALIBRATION_BAR_PX,
): number => {
  const fallback = normalizeDpi(autoDetectedDpi);
  if (!calibration.manualOverrideEnabled) return fallback;

  const horizontalDpi = calculateDpiFromMm(calibration.horizontalMm, barLengthPx);
  const verticalDpi = calculateDpiFromMm(calibration.verticalMm, barLengthPx);

  if (horizontalDpi && verticalDpi) {
    return normalizeDpi((horizontalDpi + verticalDpi) / 2);
  }
  if (horizontalDpi) return horizontalDpi;
  if (verticalDpi) return verticalDpi;
  return fallback;
};

export const calculateCalibrationScale = (
  effectiveDpi: number,
  baselineDpi: number,
): number => {
  const baseline = normalizeDpi(baselineDpi);
  const effective = normalizeDpi(effectiveDpi);
  return effective / baseline;
};

export const toPixelsPerMm = (dpi: number): number =>
  calculatePixelsPerMm(normalizeDpi(dpi));

export const defaultScreenCalibrationState =
  (): ScreenCalibrationSessionState => ({
    manualOverrideEnabled: false,
    horizontalMm: null,
    verticalMm: null,
  });

export const getCurrentCalibrationEnvironmentSnapshot = ():
  | ScreenCalibrationEnvironmentSnapshot
  | null => {
  if (typeof window === "undefined") return null;
  return {
    devicePixelRatio:
      typeof window.devicePixelRatio === "number" &&
      Number.isFinite(window.devicePixelRatio)
        ? window.devicePixelRatio
        : 1,
    visualViewportScale:
      typeof window.visualViewport?.scale === "number" &&
      Number.isFinite(window.visualViewport.scale)
        ? window.visualViewport.scale
        : 1,
    innerWidth:
      typeof window.innerWidth === "number" && Number.isFinite(window.innerWidth)
        ? window.innerWidth
        : 0,
    innerHeight:
      typeof window.innerHeight === "number" &&
      Number.isFinite(window.innerHeight)
        ? window.innerHeight
        : 0,
    screenWidth:
      typeof window.screen?.width === "number" &&
      Number.isFinite(window.screen.width)
        ? window.screen.width
        : 0,
    screenHeight:
      typeof window.screen?.height === "number" &&
      Number.isFinite(window.screen.height)
        ? window.screen.height
        : 0,
  };
};

export const hasCalibrationEnvironmentChanged = (
  baseline: ScreenCalibrationEnvironmentSnapshot | null,
  current: ScreenCalibrationEnvironmentSnapshot | null,
): boolean => {
  if (!baseline || !current) return false;
  const dprChanged =
    Math.abs((baseline.devicePixelRatio || 1) - (current.devicePixelRatio || 1)) >
    0.01;
  const visualViewportScaleChanged =
    Math.abs((baseline.visualViewportScale || 1) - (current.visualViewportScale || 1)) >
    0.01;
  const viewportChanged =
    Math.abs((baseline.innerWidth || 0) - (current.innerWidth || 0)) > 2 ||
    Math.abs((baseline.innerHeight || 0) - (current.innerHeight || 0)) > 2;
  const screenChanged =
    baseline.screenWidth !== current.screenWidth ||
    baseline.screenHeight !== current.screenHeight;

  return dprChanged || visualViewportScaleChanged || viewportChanged || screenChanged;
};

export const parseScreenCalibrationEnvironmentSession = (
  raw: string | null,
): ScreenCalibrationEnvironmentSnapshot | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ScreenCalibrationEnvironmentSnapshot>;
    if (
      typeof parsed.devicePixelRatio !== "number" ||
      typeof parsed.innerWidth !== "number" ||
      typeof parsed.innerHeight !== "number" ||
      typeof parsed.screenWidth !== "number" ||
      typeof parsed.screenHeight !== "number"
    ) {
      return null;
    }
    return {
      devicePixelRatio: parsed.devicePixelRatio,
      visualViewportScale:
        typeof parsed.visualViewportScale === "number" &&
        Number.isFinite(parsed.visualViewportScale)
          ? parsed.visualViewportScale
          : 1,
      innerWidth: parsed.innerWidth,
      innerHeight: parsed.innerHeight,
      screenWidth: parsed.screenWidth,
      screenHeight: parsed.screenHeight,
    };
  } catch {
    return null;
  }
};

export const parseScreenCalibrationSession = (
  raw: string | null,
): ScreenCalibrationSessionState => {
  if (!raw) return defaultScreenCalibrationState();
  try {
    const parsed = JSON.parse(raw) as Partial<ScreenCalibrationSessionState>;
    return {
      manualOverrideEnabled: Boolean(parsed.manualOverrideEnabled),
      horizontalMm: normalizeMeasuredMm(parsed.horizontalMm),
      verticalMm: normalizeMeasuredMm(parsed.verticalMm),
    };
  } catch {
    return defaultScreenCalibrationState();
  }
};
